// How far each field moves down when a printed form shows a multiline answer in full.
//
// On screen a multiline field keeps the height the form gives it and scrolls. In print
// (the finished-submission view, the print preview) it shows the whole answer, so a long
// one grows past that height and runs over whatever is drawn under it. A text field that
// scrolls on screen prints in full the same way, and is moved for the same way: below,
// "multiline" is either. The backend PDF applies the same rule in
// report_templates/submissions_report_template.html - keep the two in step.
//
// `fields` are the fields as printed, in px of the form: `top`/`bottom`/`left`/`right` of
// what each one shows (its content, not the padding round it), and `growth`, how much
// taller a multiline (or text) prints than the form draws it (0 for every other field). A
// grown multiline's `bottom` is where it ends in print.
//
// A field is under a multiline when it starts lower than the multiline and either shares
// its column or starts at or below where the multiline ends in the form. A multiline that
// grows over a field in its column moves every field under it down by its growth, so the
// spacing the form was designed with survives. One that covers nothing moves nothing: a
// field drawn beside a long answer stays where it is. A field under several takes the
// largest move - each multiline's own move counts, so stacked ones add up and side-by-side
// ones do not.
//
// Returns `{ [id]: px }` for the fields that move.
export const multilineGrowthPushes = (fields) => {
  const sharesColumn = (a, b) => a.left < b.right && b.left < a.right;
  const isUnder = (field, multiline) =>
    field.top > multiline.top && (sharesColumn(field, multiline) || field.top >= multiline.bottom - multiline.growth);
  const covers = (multiline, field) => isUnder(field, multiline) && sharesColumn(field, multiline) && field.top < multiline.bottom;

  const sorted = [...fields].sort((a, b) => a.top - b.top || a.left - b.left);
  const moves = {};
  const pushing = [];

  sorted.forEach((field) => {
    // Every multiline `field` can be under starts higher, so it was placed already.
    moves[field.id] = pushing.reduce(
      (move, multiline) => (isUnder(field, multiline) ? Math.max(move, moves[multiline.id] + multiline.growth) : move),
      0,
    );

    if (field.growth > 0 && fields.some((other) => covers(field, other))) {
      pushing.push(field);
    }
  });

  return Object.fromEntries(Object.entries(moves).filter(([, move]) => move > 0));
};
